import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import moment from 'moment'
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'
import EmptyState from '../../../components/EmptyState/EmptyState'
import EmployeeSelector from '../../events/EmployeeSelector'
import DocumentsField from '../../../helpers/DocumentsField'
import { uploadDocument } from '../../../helpers/uploadDocument'
import useDocumentPreview from '../../../components/Documents/useDocumentPreview'

const PRESETS = [
  {
    key: 'open',
    label: 'Open',
    caption: 'Awaiting completion',
    dot: 'info.main',
  },
  {
    key: 'overdue',
    label: 'Overdue',
    caption: 'Past their due date',
    dot: 'error.main',
  },
  {
    key: 'completed',
    label: 'Completed',
    caption: 'Closed with notes',
    dot: 'success.main',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    caption: 'Closed without action',
    dot: 'text.disabled',
  },
  {
    key: 'all',
    label: 'All Actions',
    caption: 'Everything on record',
    dot: 'primary.main',
  },
]

const SOURCE_LABELS = {
  Audit: 'Audit',
  AdverseIncident: 'Incident',
  Meeting: 'Meeting',
  RiskRegister: 'Risk Register',
  Complaint: 'Complaint',
  KPIReview: 'KPI Review',
  Manual: 'Manual',
  CQC: 'CQC Centre',
}

const STATUS_CHIP = {
  Open: 'info',
  'In Progress': 'primary',
  Overdue: 'error',
  Completed: 'success',
  Cancelled: 'default',
}

const PRIORITY_CHIP = {
  Low: 'default',
  Medium: 'info',
  High: 'warning',
  Critical: 'error',
}

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical']

// Matches the server's overdue definition (helpers/governanceActions.js):
// explicitly Overdue, or still open past its due date.
const isOverdue = (action) =>
  action.status === 'Overdue' ||
  (['Open', 'In Progress'].includes(action.status) &&
    action.dueDate &&
    moment(action.dueDate).isBefore(moment()))

const isClosed = (action) => ['Completed', 'Cancelled'].includes(action.status)

const personName = (person) =>
  person ? `${person.firstName} ${person.lastName}` : null

const PersonCell = ({ person, fallback = '—' }) =>
  person ? (
    <Stack direction="row" alignItems="center" gap={0.75}>
      <Avatar src={person.photograph} sx={{ width: 22, height: 22 }} />
      <Typography variant="body2">{personName(person)}</Typography>
    </Stack>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {fallback}
    </Typography>
  )

const PresetTile = ({ preset, count, active, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      cursor: 'pointer',
      minWidth: 0,
      borderColor: active ? 'primary.main' : 'divider',
      borderWidth: active ? 2 : 1,
      bgcolor: active ? 'action.hover' : 'background.paper',
      transition: 'all 0.15s ease',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {preset.label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: preset.dot }}
      />
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }} noWrap>
        {count ?? '—'}
      </Typography>
    </Stack>
    <Typography component="div" variant="caption" color="text.secondary" noWrap>
      {preset.caption}
    </Typography>
  </Card>
)

const DetailRow = ({ label, children }) => (
  <Stack direction="row" gap={1} alignItems="baseline">
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width: 110, flexShrink: 0, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Box flex={1} minWidth={0}>
      {children}
    </Box>
  </Stack>
)

// Create a Manual action by hand - everything else arrives from audits,
// incidents, the CQC Centre and the other governance sources.
const AddActionDialog = ({ open, onClose, onSaved }) => {
  const { setToast } = useContext(GlobalContext)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assignee, setAssignee] = useState(null)
  const [dueDate, setDueDate] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setTitle('')
    setDescription('')
    setAssignee(null)
    setDueDate('')
    setPriority('Medium')
  }

  const save = async () => {
    setSaving(true)
    const res = await API.post('/governance/actions', {
      title,
      description,
      assignedTo: assignee?._id,
      dueDate: dueDate || undefined,
      priority,
    })
    setSaving(false)
    if (res.status !== 200) {
      setToast({
        message: res.data?.error ?? 'Unable to add the action',
        severity: 'error',
      })
      return
    }
    reset()
    onSaved()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add a Governance Action</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            size="small"
          />
          <EmployeeSelector
            value={assignee}
            onChange={(e, newValue) => setAssignee(newValue)}
            label="Assign to"
            size="small"
            fullWidth
          />
          <Stack direction="row" gap={2}>
            <TextField
              label="Due date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Priority"
              select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              size="small"
              fullWidth
            >
              {PRIORITIES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !title.trim()}
        >
          {saving ? 'Saving…' : 'Add Action'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// One dialog serves viewing, editing, completing and cancelling an action -
// `mode` switches the body so the context (title, source, dates) stays put.
const ActionDialog = ({ action, onClose, onChanged }) => {
  const { setToast } = useContext(GlobalContext)
  const [mode, setMode] = useState('view') // view | edit | complete | cancel
  const [saving, setSaving] = useState(false)
  const { openPreview, previewModal } = useDocumentPreview()

  // Edit fields
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assignee, setAssignee] = useState(null)
  const [dueDate, setDueDate] = useState('')
  const [priority, setPriority] = useState('Medium')
  const [status, setStatus] = useState('Open')

  // Completion / cancellation fields
  const [completionNotes, setCompletionNotes] = useState('')
  const [resolutionNote, setResolutionNote] = useState('')
  const [evidenceUploads, setEvidenceUploads] = useState([])
  const [cancelReason, setCancelReason] = useState('')

  useEffect(() => {
    if (!action) return
    setMode('view')
    setTitle(action.title ?? '')
    setDescription(action.description ?? '')
    setAssignee(action.assignedTo ?? null)
    setDueDate(action.dueDate ? moment(action.dueDate).format('YYYY-MM-DD') : '')
    setPriority(action.priority ?? 'Medium')
    setStatus(['Open', 'In Progress'].includes(action.status) ? action.status : 'Open')
    setCompletionNotes('')
    setResolutionNote('')
    setEvidenceUploads([])
    setCancelReason('')
  }, [action])

  if (!action) return null

  const fail = (res, fallback) =>
    setToast({ message: res?.data?.error ?? fallback, severity: 'error' })

  const saveEdit = async () => {
    setSaving(true)
    const res = await API.patch(`/governance/actions/${action._id}`, {
      title,
      description,
      assignedTo: assignee?._id ?? null,
      dueDate: dueDate || null,
      priority,
      status,
    })
    setSaving(false)
    if (res.status !== 200) return fail(res, 'Unable to save the changes')
    onChanged()
  }

  const complete = async () => {
    setSaving(true)
    try {
      const evidenceFiles = []
      for (const file of evidenceUploads) {
        const key = await uploadDocument(file, 'governance/actions')
        evidenceFiles.push({ fileName: file.name, fileURL: key })
      }
      const res = await API.post(`/governance/actions/${action._id}/complete`, {
        completionNotes,
        resolutionNote: resolutionNote || undefined,
        evidenceFiles,
      })
      if (res.status !== 200) return fail(res, 'Unable to complete the action')
      onChanged()
    } catch (e) {
      setToast({ message: e.message, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const cancel = async () => {
    setSaving(true)
    const res = await API.post(`/governance/actions/${action._id}/cancel`, {
      reason: cancelReason,
    })
    setSaving(false)
    if (res.status !== 200) return fail(res, 'Unable to cancel the action')
    onChanged()
  }

  const reopen = async () => {
    setSaving(true)
    const res = await API.patch(`/governance/actions/${action._id}`, {
      status: 'Open',
    })
    setSaving(false)
    if (res.status !== 200) return fail(res, 'Unable to reopen the action')
    onChanged()
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography component="span" variant="inherit" sx={{ flex: 1, minWidth: 200 }}>
            {action.title}
          </Typography>
          <Chip
            size="small"
            label={isOverdue(action) ? 'Overdue' : action.status}
            color={isOverdue(action) ? 'error' : STATUS_CHIP[action.status]}
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack gap={1.25} pt={0.5}>
          {action.description && mode !== 'edit' && (
            <Typography variant="body2">{action.description}</Typography>
          )}
          <DetailRow label="Source">
            <Chip
              size="small"
              variant="outlined"
              label={SOURCE_LABELS[action.sourceType] ?? action.sourceType}
            />
          </DetailRow>
          <DetailRow label="Raised">
            <Typography variant="body2">
              {moment(action.createdAt).format('Do MMM YYYY')}
              {action.createdBy ? ` by ${personName(action.createdBy)}` : ''}
            </Typography>
          </DetailRow>
          {mode !== 'edit' && (
            <>
              <DetailRow label="Assigned to">
                <PersonCell person={action.assignedTo} fallback="Unassigned" />
              </DetailRow>
              <DetailRow label="Due">
                <Typography
                  variant="body2"
                  color={isOverdue(action) ? 'error.main' : 'text.primary'}
                >
                  {action.dueDate
                    ? moment(action.dueDate).format('Do MMM YYYY')
                    : 'No due date'}
                </Typography>
              </DetailRow>
              <DetailRow label="Priority">
                <Chip
                  size="small"
                  label={action.priority ?? 'Medium'}
                  color={PRIORITY_CHIP[action.priority] ?? 'default'}
                  variant="outlined"
                />
              </DetailRow>
            </>
          )}

          {isClosed(action) && (
            <>
              <Divider />
              <DetailRow label={action.status === 'Completed' ? 'Completed' : 'Cancelled'}>
                <Typography variant="body2">
                  {action.completedAt
                    ? moment(action.completedAt).format('Do MMM YYYY')
                    : '—'}
                  {action.completedBy ? ` by ${personName(action.completedBy)}` : ''}
                </Typography>
              </DetailRow>
              {action.completionNotes && (
                <DetailRow label={action.status === 'Completed' ? 'Notes' : 'Reason'}>
                  <Typography variant="body2">{action.completionNotes}</Typography>
                </DetailRow>
              )}
              {action.resolutionNote && (
                <DetailRow label="Resolution">
                  <Typography variant="body2">{action.resolutionNote}</Typography>
                </DetailRow>
              )}
              {action.evidenceFiles?.length > 0 && (
                <DetailRow label="Evidence">
                  <Stack gap={0.5}>
                    {action.evidenceFiles.map((file) => (
                      <Stack
                        key={file.fileURL}
                        direction="row"
                        alignItems="center"
                        gap={0.5}
                      >
                        <Typography variant="body2" noWrap title={file.fileName}>
                          {file.fileName ?? 'Evidence file'}
                        </Typography>
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openPreview({ key: file.fileURL, name: file.fileName })
                            }
                          >
                            <VisibilityOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                </DetailRow>
              )}
            </>
          )}

          {mode === 'edit' && (
            <>
              <Divider />
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                fullWidth
                size="small"
              />
              <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
              <EmployeeSelector
                value={assignee}
                onChange={(e, newValue) => setAssignee(newValue)}
                label="Assign to"
                size="small"
                fullWidth
              />
              <Stack direction="row" gap={2}>
                <TextField
                  label="Due date"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Priority"
                  select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  size="small"
                  fullWidth
                >
                  {PRIORITIES.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Status"
                  select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                </TextField>
              </Stack>
            </>
          )}

          {mode === 'complete' && (
            <>
              <Divider />
              <TextField
                label="What was done?"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                required
                fullWidth
                multiline
                minRows={2}
                size="small"
                helperText="Completion notes are kept as the governance record of how this action was closed."
              />
              {action.sourceType === 'CQC' && (
                <TextField
                  label="Resolution note"
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  helperText="Shown against the CQC gap this action was raised from."
                />
              )}
              <DocumentsField
                files={evidenceUploads}
                onFilesChange={setEvidenceUploads}
                disabled={saving}
                caption="Evidence of completion — PDF, Word or images"
                pendingLabel="Uploads when you complete the action"
              />
            </>
          )}

          {mode === 'cancel' && (
            <>
              <Divider />
              <TextField
                label="Why is this action being cancelled?"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                required
                fullWidth
                multiline
                minRows={2}
                size="small"
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {mode === 'view' && (
          <>
            <Button onClick={onClose}>Close</Button>
            {isClosed(action) ? (
              <Button onClick={reopen} disabled={saving}>
                Reopen
              </Button>
            ) : (
              <>
                <Button color="error" onClick={() => setMode('cancel')}>
                  Cancel Action
                </Button>
                <Button onClick={() => setMode('edit')}>Edit</Button>
                <Button variant="contained" onClick={() => setMode('complete')}>
                  Complete
                </Button>
              </>
            )}
          </>
        )}
        {mode === 'edit' && (
          <>
            <Button onClick={() => setMode('view')} disabled={saving}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={saveEdit}
              disabled={saving || !title.trim()}
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </>
        )}
        {mode === 'complete' && (
          <>
            <Button onClick={() => setMode('view')} disabled={saving}>
              Back
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={complete}
              disabled={saving || !completionNotes.trim()}
            >
              {saving ? 'Saving…' : 'Mark Completed'}
            </Button>
          </>
        )}
        {mode === 'cancel' && (
          <>
            <Button onClick={() => setMode('view')} disabled={saving}>
              Back
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={cancel}
              disabled={saving || !cancelReason.trim()}
            >
              {saving ? 'Saving…' : 'Cancel Action'}
            </Button>
          </>
        )}
      </DialogActions>
      {previewModal}
    </Dialog>
  )
}

// Register of governance actions from audits, incidents, meetings, the risk
// register and the CQC Centre. The KPI tiles and CQC gap links land here with
// a ?status= filter preset.
const GovernanceActions = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlStatus = searchParams.get('status')
  const activePreset = PRESETS.some((p) => p.key === urlStatus) ? urlStatus : 'open'

  const [loading, setLoading] = useState(true)
  const [actions, setActions] = useState([])
  const [summary, setSummary] = useState(null)
  const [search, setSearch] = useState('')
  const [sourceType, setSourceType] = useState('all')
  const [selected, setSelected] = useState(null)
  const [adding, setAdding] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    API.get('/governance/actions', { params: { status: activePreset } })
      .then((res) => {
        if (res.status === 200) {
          setActions(res.data.actions ?? [])
          setSummary(res.data.summary ?? null)
        }
      })
      .finally(() => setLoading(false))
  }, [activePreset, refreshKey])

  const refresh = () => {
    setSelected(null)
    setAdding(false)
    setRefreshKey((key) => key + 1)
  }

  const summaryCount = (preset) =>
    !summary ? null : preset.key === 'all' ? summary.total : summary[preset.key]

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return actions.filter((action) => {
      if (sourceType !== 'all' && action.sourceType !== sourceType) return false
      if (!q) return true
      return [
        action.title,
        action.description,
        SOURCE_LABELS[action.sourceType],
        personName(action.assignedTo),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    })
  }, [actions, search, sourceType])

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" gap={1} alignItems="flex-start">
        <AssignmentTurnedInIcon color="primary" sx={{ mt: 0.8 }} />
        <Stack flex={1}>
          <Typography variant="title2">Governance Actions</Typography>
          <Typography variant="caption" color="text.secondary">
            Actions raised from audits, incidents, meetings, the risk register
            and the CQC Centre. The KPI counts on the Clinical Governance
            dashboard are drawn from this register.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAdding(true)}
          sx={{ flexShrink: 0 }}
        >
          Add Action
        </Button>
      </Stack>

      <Box
        display="grid"
        gap={1.5}
        sx={{
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(5, 1fr)',
          },
        }}
      >
        {PRESETS.map((preset) => (
          <PresetTile
            key={preset.key}
            preset={preset}
            count={summaryCount(preset)}
            active={activePreset === preset.key}
            onClick={() => setSearchParams({ status: preset.key })}
          />
        ))}
      </Box>

      <Stack direction="row" gap={1.5} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Search by title, wording or assignee"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 420, flex: 1, minWidth: 220 }}
        />
        <TextField
          size="small"
          select
          label="Source"
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="all">All sources</MenuItem>
          {Object.entries(SOURCE_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {loading && <LinearProgress />}

      {!loading && filtered.length === 0 ? (
        <EmptyState
          title="No governance actions here"
          description={
            search || sourceType !== 'all'
              ? 'Nothing matches your filters.'
              : 'Nothing sits under this filter. Actions are raised from audit answers, incidents and the CQC Centre, or added by hand with Add Action.'
          }
        />
      ) : (
        !loading && (
          <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Assigned to</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((action) => (
                  <TableRow
                    key={action._id}
                    hover
                    onClick={() => setSelected(action)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={action.title}>
                        {action.title}
                      </Typography>
                      {action.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                          title={action.description}
                        >
                          {action.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={SOURCE_LABELS[action.sourceType] ?? action.sourceType}
                      />
                    </TableCell>
                    <TableCell>
                      <PersonCell person={action.assignedTo} fallback="Unassigned" />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={isOverdue(action) ? 'error.main' : 'text.primary'}
                        fontWeight={isOverdue(action) ? 600 : 400}
                      >
                        {action.dueDate ? moment(action.dueDate).format('D MMM YYYY') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={action.priority ?? 'Medium'}
                        color={PRIORITY_CHIP[action.priority] ?? 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={isOverdue(action) ? 'Overdue' : action.status}
                        color={isOverdue(action) ? 'error' : STATUS_CHIP[action.status]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )
      )}

      <AddActionDialog open={adding} onClose={() => setAdding(false)} onSaved={refresh} />
      <ActionDialog action={selected} onClose={() => setSelected(null)} onChanged={refresh} />
    </Box>
  )
}

export default GovernanceActions
